import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText,
  Upload,
  Download,
  Eye,
  Trash2,
  ExternalLink,
  AlertTriangle,
  CheckCircle2,
  FolderOpen,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import apiService from '@/services/api';

// Document types with validation rules
const DOCUMENT_TYPES = {
  EICR: { label: 'Electrical Installation Condition Report', expiryMonths: 60, color: 'bg-blue-100' },
  EPC: { label: 'Energy Performance Certificate', expiryMonths: 120, color: 'bg-green-100' },
  GAS: { label: 'Gas Safety Certificate', expiryMonths: 12, color: 'bg-amber-100' },
  INSURANCE: { label: 'Buildings Insurance', expiryMonths: 12, color: 'bg-purple-100' },
  INVENTORY: { label: 'Property Inventory', expiryMonths: null, color: 'bg-gray-100' },
  RECEIPT: { label: 'Receipt/Invoice', expiryMonths: null, color: 'bg-slate-100' },
  OTHER: { label: 'Other Document', expiryMonths: null, color: 'bg-white' }
};

interface Property {
  id: string;
  name: string;
  address: string;
}

interface VaultDocument {
  id: string;
  propertyId: string;
  propertyName: string;
  fileName: string;
  documentType: keyof typeof DOCUMENT_TYPES;
  uploadDate: string;
  expiryDate?: string;
  fileSize: number;
  fileUrl?: string;
  status: 'active' | 'expiring_soon' | 'expired';
  uploadedBy: string;
}

interface DocumentVaultSectionProps {
  limit?: number;
}

const DocumentVaultSection: React.FC<DocumentVaultSectionProps> = ({ limit = 3 }) => {
  const { toast } = useToast();

  const [documents, setDocuments] = useState<VaultDocument[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<string>('');
  const [selectedDocType, setSelectedDocType] = useState<keyof typeof DOCUMENT_TYPES>('OTHER');
  const [expiryDate, setExpiryDate] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [docsRes, propsRes] = await Promise.all([
        apiService.get('/customer/documents'),
        apiService.get('/customer/properties')
      ]);

      if (docsRes?.data) setDocuments(docsRes.data);
      if (propsRes?.data) setProperties(propsRes.data);
    } catch (error) {
      console.error('Error loading documents:', error);
      toast({
        title: 'Error',
        description: 'Failed to load documents',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getDocumentStatus = (expiryDate?: string): VaultDocument['status'] => {
    if (!expiryDate) return 'active';
    
    const expiry = new Date(expiryDate);
    const today = new Date();
    const daysUntilExpiry = Math.floor((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) return 'expired';
    if (daysUntilExpiry <= 30) return 'expiring_soon';
    return 'active';
  };

  const calculateExpiryDate = (docType: keyof typeof DOCUMENT_TYPES): string => {
    const months = DOCUMENT_TYPES[docType].expiryMonths;
    if (!months) return '';

    const date = new Date();
    date.setMonth(date.getMonth() + months);
    return date.toISOString().split('T')[0];
  };

  const handleDocumentTypeChange = (type: keyof typeof DOCUMENT_TYPES) => {
    setSelectedDocType(type);
    const calculatedDate = calculateExpiryDate(type);
    setExpiryDate(calculatedDate);
  };

  const handleUpload = async () => {
    if (!selectedProperty || !selectedFile) {
      toast({
        title: 'Validation Error',
        description: 'Please select a property and file',
        variant: 'destructive'
      });
      return;
    }

    // Validate file type
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!validTypes.includes(selectedFile.type)) {
      toast({
        title: 'Invalid File Type',
        description: 'Please upload PDF, JPG, or PNG files only',
        variant: 'destructive'
      });
      return;
    }

    try {
      setUploading(true);
      
      // Send document metadata
      const response = await apiService.post('/customer/documents/upload', {
        propertyId: selectedProperty,
        documentType: selectedDocType,
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        mimeType: selectedFile.type,
        expiryDate: expiryDate || null,
        notes: `Uploaded on ${new Date().toLocaleDateString()}`
      });

      if (response.success) {
        toast({
          title: 'Success',
          description: 'Document uploaded successfully',
        });
        setShowUploadDialog(false);
        setSelectedFile(null);
        setSelectedProperty('');
        setExpiryDate('');
        loadData();
      }
    } catch (error: any) {
      toast({
        title: 'Upload Failed',
        description: error.message || 'Failed to upload document',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      await apiService.post(`/customer/documents/${docId}/delete`, {});
      toast({
        title: 'Deleted',
        description: 'Document deleted successfully',
      });
      loadData();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'Failed to delete document',
        variant: 'destructive'
      });
    }
  };

  const handleDownload = (doc: VaultDocument) => {
    if (doc.fileUrl) {
      window.open(doc.fileUrl, '_blank');
    }
  };

  const getStatusBadge = (status: VaultDocument['status']) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle2 className="w-3 h-3 mr-1" /> Active</Badge>;
      case 'expiring_soon':
        return <Badge className="bg-amber-100 text-amber-800"><AlertTriangle className="w-3 h-3 mr-1" /> Expiring</Badge>;
      case 'expired':
        return <Badge className="bg-red-100 text-red-800"><AlertTriangle className="w-3 h-3 mr-1" /> Expired</Badge>;
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const expiringDocuments = documents.filter(d => d.status !== 'active');
  const displayDocuments = documents.slice(0, limit);
  const hasMore = documents.length > limit;

  if (loading) {
    return (
      <Card className="dashboard-card">
        <CardHeader className="dashboard-card-header">
          <CardTitle className="dashboard-card-title">Document Vault</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="dashboard-card">
      <CardHeader className="dashboard-card-header">
        <div className="flex items-center justify-between w-full">
          <CardTitle className="dashboard-card-title">Document Vault</CardTitle>
          <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2">
                <Upload className="w-4 h-4" />
                Upload
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Upload Document</DialogTitle>
                <DialogDescription>
                  Upload compliance certificates and property documents
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="property">Property *</Label>
                  {properties.length === 0 ? (
                    <p className="text-sm text-muted-foreground mt-2">No properties found</p>
                  ) : (
                    <select
                      id="property"
                      value={selectedProperty}
                      onChange={(e) => setSelectedProperty(e.target.value)}
                      className="w-full border rounded-lg p-2 mt-2"
                    >
                      <option value="">Select a property...</option>
                      {properties.map(prop => (
                        <option key={prop.id} value={prop.id}>
                          {prop.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div>
                  <Label htmlFor="doctype">Document Type *</Label>
                  <select
                    id="doctype"
                    value={selectedDocType}
                    onChange={(e) => handleDocumentTypeChange(e.target.value as keyof typeof DOCUMENT_TYPES)}
                    className="w-full border rounded-lg p-2 mt-2"
                  >
                    {Object.entries(DOCUMENT_TYPES).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="file">Select File *</Label>
                  <input
                    id="file"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    className="w-full border rounded-lg p-2 mt-2"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handleUpload}
                    disabled={uploading || !selectedProperty || !selectedFile}
                    className="flex-1"
                  >
                    {uploading ? 'Uploading...' : 'Upload'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowUploadDialog(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {expiringDocuments.length > 0 && (
          <div className="bg-amber-50 border-b border-amber-200 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-amber-900">
                  {expiringDocuments.length} document(s) need attention
                </p>
              </div>
            </div>
          </div>
        )}

        {documents.length === 0 ? (
          <div className="p-8 text-center">
            <FolderOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground mb-4">No documents uploaded yet</p>
            <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
              <DialogTrigger asChild>
                <Button>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Your First Document
                </Button>
              </DialogTrigger>
            </Dialog>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {displayDocuments.map(doc => (
              <div
                key={doc.id}
                className="p-4 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`p-2 rounded flex-shrink-0 ${DOCUMENT_TYPES[doc.documentType].color}`}>
                      <FileText className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="dashboard-list-item-label font-semibold truncate">
                        {doc.fileName}
                      </h4>
                      <p className="dashboard-form-helper text-muted-foreground">
                        {DOCUMENT_TYPES[doc.documentType].label}
                      </p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {getStatusBadge(doc.status)}
                  </div>
                </div>

                <div className="flex gap-2">
                  {doc.fileUrl && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-2 flex-1"
                        onClick={() => handleDownload(doc)}
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-2 flex-1"
                        onClick={() => window.open(doc.fileUrl, '_blank')}
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </Button>
                    </>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(doc.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            
            {hasMore && (
              <div className="p-4 text-center border-t border-border">
                <Link to="/documents">
                  <Button variant="outline" className="gap-2">
                    View All Documents
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DocumentVaultSection;
